import { EntityAudit } from "@modernary/mui/components/audit";
import {
  EntityDocumentForm,
  EntityDocumentMultipleForm,
  EntityDocumentsChildList,
  EntityDocumentsListTable,
} from "@modernary/mui/components/document";
import { EntityNotes } from "@modernary/mui/components/note";
import { RouterView } from "vue-router";

/** @type {import('vue-router').RouterOptions['routes']} */

const names = {
  projects: "Project.Projects",
  list: "Project.ProjectList",
  new: "Project.ProjectNew",
  base: "Project.ProjectBase",
  view: "Project.ProjectView",
  edit: "Project.ProjectEdit",
  notes: "Project.ProjectNotes",
  noteEdit: "Project.ProjectNoteEdit",
  noteNew: "Project.ProjectNoteNew",
  documents: "Project.ProjectDocuments",
  documentEdit: "Project.ProjectDocumentEdit",
  documentNew: "Project.ProjectDocumentNew",
  audit: "Project.ProjectAudit",
  deliverables: "Project.ProjectDeliverables",
  deliverableEdit: "Project.ProjectDeliverableEdit",
  deliverableNew: "Project.ProjectDeliverableNew",
  deliverableAcceptanceForms: "Project.ProjectDeliverableAcceptanceForms",
  deliverableAcceptanceFormEdit: "Project.ProjectDeliverableAcceptanceFormEdit",
  deliverableAcceptanceFormNew: "Project.ProjectDeliverableAcceptanceFormNew",
  spendRequests: "Project.ProjectSpendRequests",
  spendRequestEdit: "Project.ProjectSpendRequestEdit",
  spendRequestNew: "Project.ProjectSpendRequestNew",
  contractTaskOrders: "Project.ProjectContractTaskOrders",
  contractTaskOrderEdit: "Project.ProjectContractTaskOrderEdit",
  contractTaskOrderNew: "Project.ProjectContractTaskOrderNew",
  purchaseOrders: "Project.ProjectPurchaseOrders",
  purchaseOrderEdit: "Project.ProjectPurchaseOrderEdit",
  purchaseOrderNew: "Project.ProjectPurchaseOrderNew",
  invoices: "Project.ProjectInvoices",
  invoiceEdit: "Project.ProjectInvoiceEdit",
  invoiceNew: "Project.ProjectInvoiceNew",
};

export { names as projectRouteNames };

const projectRoutes = [
  {
    path: "/project",
    name: names.projects,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.list },
    children: [
      {
        path: "list",
        name: names.list,
        meta: {
          title: "Projects",
          lookupGroups: [
            "ProjectStatus",
            "ProjectType",
            "TaskOrderStatus",
            "PurchaseOrderStatus",
            "SpendRequestStatus",
          ],
        },
        component: () => import("@/views/project/ProjectList.vue"),
      },
      {
        path: "new",
        component: () => import("@/views/project/ProjectBase.vue"),
        redirect: { name: names.new },
        props: true,
        children: [
          {
            path: "",
            name: names.new,
            meta: { title: "Project New", isNew: true },
            component: () => import("@/views/project/ProjectEdit.vue"),
          },
        ],
      },
      {
        path: ":id(\\d+)",
        name: names.base,
        component: () => import("@/views/project/ProjectBase.vue"),
        redirect: { name: names.view },
        children: [
          {
            path: "view",
            name: names.view,
            meta: { title: "Project View" },
            component: () => import("@/views/project/ProjectView.vue"),
          },
          {
            path: "edit",
            name: names.edit,
            meta: { title: "Project Edit", isForm: true },
            component: () => import("@/views/project/ProjectEdit.vue"),
          },
          {
            path: "notes",
            name: names.notes,
            meta: { title: "Project Notes" },
            component: {
              render: () => <EntityNotes idField="projectId" routeNamePrefix="Project.ProjectNote" />,
            },
          },
          {
            path: "notes/:annotationId(\\d+)",
            name: names.noteEdit,
            meta: { title: "Project Note Edit", isForm: true },
            component: () => import("@/views/project/ProjectNote.vue"),
          },
          {
            path: "notes/new",
            name: names.noteNew,
            meta: { title: "Project Note New", isForm: true },
            component: () => import("@/views/project/ProjectNote.vue"),
          },
          {
            path: "documents",
            name: names.documents,
            meta: { title: "Project Documents" },
            component: {
              render: () => <EntityDocumentsChildList resourceTypeKey="project" />,
            },
          },
          {
            path: "documents/:documentId(\\d+)",
            name: names.documentEdit,
            meta: { title: "Project Document Edit", isForm: true },
            component: {
              render: () => <EntityDocumentForm resourceTypeKey="project" />,
            },
          },
          {
            path: "documents/new",
            name: names.documentNew,
            meta: { title: "Project Document New", isForm: true },
            component: {
              render: () => <EntityDocumentMultipleForm resourceTypeKey="project" />,
            },
          },
          {
            path: "deliverables",
            name: names.deliverables,
            meta: { title: "Project Deliverables" },
            component: () => import("@/views/project/DeliverablesChildList.vue"),
          },
          {
            path: "deliverables/:deliverableId(\\d+)",
            name: names.deliverableEdit,
            meta: { title: "Project Deliverable Edit", isForm: true },
            component: () => import("@/views/project/ProjectDeliverable.vue"),
          },
          {
            path: "deliverables/new",
            name: names.deliverableNew,
            meta: { title: "Project Deliverable New", isForm: true },
            component: () => import("@/views/project/ProjectDeliverable.vue"),
          },
          {
            path: "deliverableAcceptanceForms",
            name: names.deliverableAcceptanceForms,
            meta: { title: "Project Deliverable Acceptance Forms" },
            component: () => import("@/views/project/ProjectDeliverableAcceptanceForms.vue"),
          },
          {
            path: "deliverableAcceptanceForms/:deliverableAcceptanceFormId(\\d+)",
            name: names.deliverableAcceptanceFormEdit,
            meta: { title: "Project Deliverable Acceptance Form Edit", isForm: true },
            component: () => import("@/views/project/ProjectDeliverableAcceptanceForm.vue"),
          },
          {
            path: "deliverableAcceptanceForms/new",
            name: names.deliverableAcceptanceFormNew,
            meta: { title: "Project Deliverable Acceptance Form New", isForm: true },
            component: () => import("@/views/project/ProjectDeliverableAcceptanceForm.vue"),
          },
          {
            path: "spendRequests/:spendRequestId(\\d+)",
            name: names.spendRequestEdit,
            meta: { title: "Project Spend Request Edit", isForm: true },
            component: () => import("@/views/project/ProjectSpendRequest.vue"),
          },
          {
            path: "spendRequests/new",
            name: names.spendRequestNew,
            meta: { title: "Project Spend Request New", isForm: true },
            component: () => import("@/views/project/ProjectSpendRequest.vue"),
          },
          {
            path: "contractTaskOrders/:contractTaskOrderId(\\d+)",
            name: names.contractTaskOrderEdit,
            meta: { title: "Project Contract Task Order Edit", isForm: true },
            component: () => import("@/views/project/ProjectContractTaskOrder.vue"),
          },
          {
            path: "contractTaskOrders/new",
            name: names.contractTaskOrderNew,
            meta: { title: "Project Contract Task Order New", isForm: true },
            component: () => import("@/views/project/ProjectContractTaskOrder.vue"),
          },
          {
            path: "purchaseOrders/:purchaseOrderId(\\d+)",
            name: names.purchaseOrderEdit,
            meta: { title: "Project Purchase Order Edit", isForm: true },
            component: () => import("@/views/project/ProjectPurchaseOrder.vue"),
          },
          {
            path: "purchaseOrders/new",
            name: names.purchaseOrderNew,
            meta: { title: "Project Purchase Order New", isForm: true },
            component: () => import("@/views/project/ProjectPurchaseOrder.vue"),
          },
          {
            path: "invoices",
            name: names.invoices,
            meta: { title: "Project Invoices" },
            component: () => import("@/views/project/ProjectInvoices.vue"),
          },
          {
            path: "invoices/:invoiceId(\\d+)",
            name: names.invoiceEdit,
            meta: { title: "Project Invoice Edit", isForm: true },
            component: () => import("@/views/project/ProjectInvoice.vue"),
          },
          {
            path: "invoice/new",
            name: names.invoiceNew,
            meta: { title: "Project Invoice New", isForm: true },
            component: () => import("@/views/project/ProjectInvoice.vue"),
          },
          {
            path: "activity",
            name: names.audit,
            meta: { title: "Project Audit Activity" },
            component: {
              render: () => <EntityAudit entityType="Project" />,
            },
          },
        ],
      },
    ],
  },
];
export default projectRoutes;
